/**
 * Keyword results VIEW state that lives in the URL — the Refine chip bar and
 * the Group-by choice — so it survives the jump to Compare and the Back from
 * it (ticket 2.4: "AB view should show the same set of drugs as what the user
 * filtered for in results view. Filter should persist.").
 *
 * Search page: `kwRefine` / `kwGroup` (prefixed so they never collide with the
 * Cogni table's own `refine` / `groupBy` params on the same URL).
 * Compare page: `refine` / `group` (its own URL, nothing to collide with).
 *
 * These are display-only params: they never change the executed search, so
 * SearchPage's keyword URL→state restore must ignore them (see
 * KW_VIEW_PARAMS) or a chip click would clobber unsubmitted form edits.
 */
import type { KeywordSearchResultItem } from '../types';
import { drugTableRefineFields, matchesRefineChips, type RefineChip } from './refineQuery';
import type { GroupByOption } from './groupResults';
import type { DrugSort, DrugSortColumn } from './drugResultsTable';

export const KW_REFINE_PARAM = 'kwRefine';
export const KW_GROUP_PARAM = 'kwGroup';
/** Search-page params that are view state only, not part of the search. */
export const KW_VIEW_PARAMS = [KW_REFINE_PARAM, KW_GROUP_PARAM] as const;

/** Compare-page-only server sort (`/compare-keyword?sort=matches:desc`). Not a
 *  Search-page view param, so deliberately absent from KW_VIEW_PARAMS. */
export const KW_SORT_PARAM = 'sort';

const SORT_COLUMNS: ReadonlySet<string> = new Set<DrugSortColumn>([
  'brand', 'generic', 'manufacturer', 'region', 'approval_date', 'matches',
]);

export function serializeSortParam(sort: DrugSort): string {
  return `${sort.column}:${sort.direction}`;
}

/** Parse a sort URL value; anything malformed or unknown is null. */
export function parseSortParam(raw: string | null): DrugSort | null {
  if (!raw) return null;
  const [column, direction, ...rest] = raw.split(':');
  if (rest.length > 0 || !column || !SORT_COLUMNS.has(column)) return null;
  if (direction !== 'asc' && direction !== 'desc') return null;
  return { column: column as DrugSortColumn, direction };
}

const GROUP_OPTIONS: ReadonlySet<string> = new Set<GroupByOption>(['none', 'generic', 'manufacturer', 'type', 'region']);

/** Parse a group-by URL value; anything unknown falls back to 'none'. */
export function parseGroupParam(raw: string | null): GroupByOption {
  return raw && GROUP_OPTIONS.has(raw) ? (raw as GroupByOption) : 'none';
}

/** The Compare rail's rows narrowed by the chips carried over from the
 *  results page — the same matcher and field adapter the results table uses,
 *  so both views agree on which drugs pass. */
export function filterByRefineChips(
  rows: KeywordSearchResultItem[],
  chips: RefineChip[],
  includeApprovalDate: boolean,
): KeywordSearchResultItem[] {
  if (chips.length === 0) return rows;
  return rows.filter((r) => matchesRefineChips(drugTableRefineFields(r, includeApprovalDate), chips));
}
